/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { Uint8ArrayToString, bufferToString, stringToBuffer, } from "@fluid-internal/client-utils";
import { assert, compareArrays, unreachableCase } from "@fluidframework/core-utils/internal";
import { SummaryType } from "@fluidframework/driver-definitions";
import { DriverErrorTypes, } from "@fluidframework/driver-definitions/internal";
import { isCombinedAppAndProtocolSummary, readAndParse, } from "@fluidframework/driver-utils/internal";
import { LoggingError, UsageError, } from "@fluidframework/telemetry-utils/internal";
import { v4 as uuid } from "uuid";
/**
 * Utility api to parse the IResolvedUrl.url into specific parts like querystring, path to get
 * deep link info etc.
 * Warning - This function may not be compatible with any Url Resolver's resolved url. It works
 * with urls of type: protocol://<string>/.../..?<querystring>
 * @param url - This is the IResolvedUrl.url part of the resolved url.
 * @returns The IParsedUrl representing the input URL, or undefined if the format was not supported
 * @legacy
 * @alpha
 */
export function tryParseCompatibleResolvedUrl(url) {
    const parsed = new URL(url);
    if (typeof parsed.pathname !== "string") {
        throw new LoggingError("Failed to parse pathname");
    }
    const query = parsed.search ?? "";
    const regex = /^\/([^/]*\/[^/]*)(\/?.*)$/;
    const match = regex.exec(parsed.pathname);
    return match?.length === 3
        ? {
            // Non null asserting here because of the length check above
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            id: match[1],
            // Non null asserting here because of the length check above
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            path: match[2],
            query,
            // URLSearchParams returns null if the param is not provided.
            version: parsed.searchParams.get("version") ?? undefined,
        }
        : undefined;
}
/**
 * Combine the app summary and protocol summary in 1 tree.
 * @param appSummary - Summary of the app.
 * @param protocolSummary - Summary of the protocol.
 * @internal
 */
export function combineAppAndProtocolSummary(appSummary, protocolSummary) {
    assert(!isCombinedAppAndProtocolSummary(appSummary), 0x5a8 /* app summary is already a combined tree! */);
    assert(!isCombinedAppAndProtocolSummary(protocolSummary), 0x5a9 /* protocol summary is already a combined tree! */);
    const createNewSummary = {
        type: SummaryType.Tree,
        tree: {
            ".protocol": protocolSummary,
            ".app": appSummary,
        },
    };
    return createNewSummary;
}
/**
 * Converts a summary to snapshot tree and separate its blob contents
 * to align detached container format with IPendingContainerState
 * @param summary - ISummaryTree
 */
function convertSummaryToSnapshotAndBlobs(summary) {
    let blobContents = {};
    const treeNode = {
        blobs: {},
        trees: {},
        id: uuid(),
        unreferenced: summary.unreferenced,
        groupId: summary.groupId,
    };
    const keys = Object.keys(summary.tree);
    for (const key of keys) {
        // TODO change this to use object.entries
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const summaryObject = summary.tree[key];
        switch (summaryObject.type) {
            case SummaryType.Tree: {
                const innerSnapshot = convertSummaryToSnapshotAndBlobs(summaryObject);
                treeNode.trees[key] = innerSnapshot.baseSnapshot;
                blobContents = { ...blobContents, ...innerSnapshot.snapshotBlobs };
                break;
            }
            case SummaryType.Attachment: {
                treeNode.blobs[key] = summaryObject.id;
                break;
            }
            case SummaryType.Blob: {
                const blobId = uuid();
                treeNode.blobs[key] = blobId;
                const contentString = summaryObject.content instanceof Uint8Array
                    ? Uint8ArrayToString(summaryObject.content)
                    : summaryObject.content;
                blobContents[blobId] = contentString;
                break;
            }
            case SummaryType.Handle: {
                throw new LoggingError("No handles should be there in summary in detached container!!");
            }
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
                unreachableCase(summaryObject, `Unknown tree type ${summaryObject.type}`);
            }
        }
    }
    const pendingSnapshot = { baseSnapshot: treeNode, snapshotBlobs: blobContents };
    return pendingSnapshot;
}
/**
 * Converts a snapshot to snapshotInfo with its blob contents
 * to align detached container format with IPendingContainerState
 *
 * Note, this assumes the ISnapshot sequence number is defined. Otherwise an assert will be thrown
 * @param snapshot - ISnapshot
 */
export function convertSnapshotToSnapshotInfo(snapshot) {
    assert(snapshot.sequenceNumber !== undefined, 0x93a /* Snapshot sequence number is missing */);
    const snapshotBlobs = {};
    for (const [blobId, arrayBufferLike] of snapshot.blobContents.entries()) {
        snapshotBlobs[blobId] = bufferToString(arrayBufferLike, "utf8");
    }
    return {
        baseSnapshot: snapshot.snapshotTree,
        snapshotBlobs,
        snapshotSequenceNumber: snapshot.sequenceNumber,
    };
}
/**
 * Converts a snapshot to snapshotInfo with its blob contents
 * to align detached container format with IPendingContainerState
 *
 * Note, this assumes the ISnapshot sequence number is defined. Otherwise an assert will be thrown
 * @param snapshot - ISnapshot
 */
export function convertSnapshotInfoToSnapshot(snapshotInfo, snapshotSequenceNumber) {
    const blobContents = new Map();
    for (const [blobId, serializedContent] of Object.entries(snapshotInfo.snapshotBlobs)) {
        blobContents.set(blobId, stringToBuffer(serializedContent, "utf8"));
    }
    return {
        snapshotTree: snapshotInfo.baseSnapshot,
        blobContents,
        ops: [],
        sequenceNumber: snapshotSequenceNumber,
        latestSequenceNumber: undefined,
        snapshotFormatV: 1,
    };
}
/**
 * Converts summary parts into a SnapshotTree and its blob contents.
 * @param protocolSummaryTree - Protocol Summary Tree
 * @param appSummaryTree - App Summary Tree
 */
function convertProtocolAndAppSummaryToSnapshotAndBlobs(protocolSummaryTree, appSummaryTree) {
    const combinedSummary = {
        type: SummaryType.Tree,
        tree: { ...appSummaryTree.tree },
    };
    combinedSummary.tree[".protocol"] = protocolSummaryTree;
    const snapshotTreeWithBlobContents = convertSummaryToSnapshotAndBlobs(combinedSummary);
    return snapshotTreeWithBlobContents;
}
export const getSnapshotTreeAndBlobsFromSerializedContainer = (detachedContainerSnapshot) => {
    assert(isCombinedAppAndProtocolSummary(detachedContainerSnapshot), 0x8e6 /* Protocol and App summary trees should be present */);
    const protocolSummaryTree = detachedContainerSnapshot.tree[".protocol"];
    const appSummaryTree = detachedContainerSnapshot.tree[".app"];
    const snapshotTreeWithBlobContents = convertProtocolAndAppSummaryToSnapshotAndBlobs(protocolSummaryTree, appSummaryTree);
    return snapshotTreeWithBlobContents;
};
export function getProtocolSnapshotTree(snapshot) {
    return ".protocol" in snapshot.trees ? snapshot.trees[".protocol"] : snapshot;
}
export const combineSnapshotTreeAndSnapshotBlobs = (baseSnapshot, snapshotBlobs) => {
    const blobsContents = {};
    // Process blobs in the current level
    for (const [, id] of Object.entries(baseSnapshot.blobs)) {
        const snapshot = snapshotBlobs[id];
        if (snapshot !== undefined) {
            blobsContents[id] = stringToBuffer(snapshot, "utf8");
        }
    }
    // Recursively process trees in the current level
    const trees = {};
    for (const [path, tree] of Object.entries(baseSnapshot.trees)) {
        trees[path] = combineSnapshotTreeAndSnapshotBlobs(tree, snapshotBlobs);
    }
    // Create a new snapshot tree with blob contents and processed trees
    const snapshotTreeWithBlobContents = {
        ...baseSnapshot,
        blobsContents,
        trees,
    };
    return snapshotTreeWithBlobContents;
};
export function isDeltaStreamConnectionForbiddenError(error) {
    return (typeof error === "object" &&
        error !== null &&
        error?.errorType ===
            DriverErrorTypes.deltaStreamConnectionForbidden);
}
/**
 * Validates format in parsed string get from detached container
 * serialization using IPendingDetachedContainerState format.
 */
function isPendingDetachedContainerState(detachedContainerState) {
    if (detachedContainerState?.attached === undefined ||
        detachedContainerState?.baseSnapshot === undefined ||
        detachedContainerState?.snapshotBlobs === undefined ||
        detachedContainerState?.hasAttachmentBlobs === undefined) {
        return false;
    }
    return true;
}
/**
 * Parses the given string into {@link IPendingDetachedContainerState} format,
 * with validation (if invalid, throws a UsageError).
 * This is the inverse of the JSON.stringify call in {@link Container.serialize}
 */
export function getDetachedContainerStateFromSerializedContainer(serializedContainer) {
    const hasBlobsSummaryTree = ".hasAttachmentBlobs";
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const parsedContainerState = JSON.parse(serializedContainer);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    if (isPendingDetachedContainerState(parsedContainerState)) {
        return parsedContainerState;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    }
    else if (isCombinedAppAndProtocolSummary(parsedContainerState)) {
        const { baseSnapshot, snapshotBlobs } = getSnapshotTreeAndBlobsFromSerializedContainer(parsedContainerState);
        const detachedContainerState = {
            attached: false,
            baseSnapshot,
            snapshotBlobs,
            hasAttachmentBlobs: parsedContainerState.tree[hasBlobsSummaryTree] !== undefined,
        };
        return detachedContainerState;
    }
    else {
        throw new UsageError("Cannot rehydrate detached container. Incorrect format");
    }
}
/**
 * Blindly parses the given string into {@link IPendingContainerState} format.
 * This is the inverse of the JSON.stringify call in {@link SerializedStateManager.getPendingLocalState}
 */
export function getAttachedContainerStateFromSerializedContainer(serializedContainer) {
    return serializedContainer === undefined
        ? undefined
        : JSON.parse(serializedContainer);
}
/**
 * Ensures only a single instance of the provided async function is running.
 * If there are multiple calls they will all get the same promise to wait on.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const runSingle = (func) => {
    let running;
    // don't mark this function async, so we return the same promise,
    // rather than one that is wrapped due to async
    // eslint-disable-next-line @typescript-eslint/promise-function-async
    return (...args) => {
        if (running !== undefined) {
            if (!compareArrays(running.args, args)) {
                return Promise.reject(new UsageError("Subsequent calls cannot use different arguments."));
            }
            return running.result;
        }
        running = { args, result: func(...args).finally(() => (running = undefined)) };
        return running.result;
    };
};
export async function getDocumentAttributes(storage, tree) {
    if (tree === undefined) {
        return {
            minimumSequenceNumber: 0,
            sequenceNumber: 0,
        };
    }
    // Backward compatibility: old docs would have ".attributes" instead of "attributes"
    const attributesHash = ".protocol" in tree.trees
        ? tree.trees[".protocol"].blobs.attributes
        : tree.blobs[".attributes"];
    // Non null asserting here because of the length check above
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const attributes = await readAndParse(storage, attributesHash);
    return attributes;
}
//# sourceMappingURL=utils.js.map